import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  Play, 
  Clock, 
  Award,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Assessment, Assessments } from '../../types';

interface AssessmentButtonProps {
  assessments: Assessments;
  onStartAssessment: (assessmentId: string, assessment: Assessment) => void;
}

export const AssessmentButton: React.FC<AssessmentButtonProps> = ({
  assessments,
  onStartAssessment
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const assessmentEntries = Object.entries(assessments);

  if (assessmentEntries.length === 0) {
    return null;
  }

  const formatAssessmentName = (id: string) => {
    return id
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="relative">
      {/* Main Assessment Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-3 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors w-full sm:w-auto"
      >
        <BarChart3 className="w-4 h-4 sm:w-4 sm:h-4" />
        <span className="font-medium text-sm sm:text-sm">Assessments</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 sm:w-4 sm:h-4" />
        ) : (
          <ChevronDown className="w-4 h-4 sm:w-4 sm:h-4" />
        )}
      </button>

      {/* Assessment Dropdown */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-full left-0 mt-2 w-full sm:w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-40"
          >
            <div className="p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Available Assessments</h3>
              
              <div className="space-y-2 sm:space-y-3">
                {assessmentEntries
                  .filter(([id, assessment]) => assessment && assessment.items)
                  .map(([id, assessment]) => (
                  <div
                    key={id}
                    className="bg-gray-50 rounded-lg p-2 sm:p-3 hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => {
                      onStartAssessment(id, assessment);
                      setIsExpanded(false);
                    }}
                  >
                    <div className="flex items-center justify-between mb-1 sm:mb-2">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base">
                        {formatAssessmentName(id)}
                      </h4>
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-0">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <Clock className="w-2 h-2 sm:w-3 sm:h-3" />
                        <span>{assessment.items?.length || 0} questions</span>
                      </div>
                      
                      {assessment.scoring?.mastery_threshold && assessment.scoring?.max && (
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <Award className="w-2 h-2 sm:w-3 sm:h-3" />
                          <span>
                            {assessment.scoring.mastery_threshold}/{assessment.scoring.max} to master
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
